'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import SVGMyLeagues from './icons/SVGMyLeagues';
import SVGMyTournament from './icons/SVGMyTournament';
import getCDNURL from './utils/getCDNURL';
import { User } from './utils/types';

import { Avatar } from '@pickleballinc/react-ui';

import { faAngleDown, faCreditCard, faSignOut, faSquarePollVertical, faTableColumns, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface UserProfileItemProps {
	label: string;
	onClick: () => void;
	testId?: string;
	children?: React.ReactNode;
}

function UserProfileItem({ label, onClick, testId, children }: UserProfileItemProps) {
	return (
		<div onClick={onClick} className="!outline-none">
			<div
				className="cursor-pointer rounded p-2 !outline-none hover:bg-gray-100 flex items-center gap-2"
				data-testid={testId}
				style={{
					fontFamily:
						"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'"
				}}
			>
				{children}
				{label}
			</div>
		</div>
	);
}

interface UserProfileProps {
	pickleballHref: string;
	tournamentHref: string;
	leaguesHref: string;
	ssoLink: string;
	user: User;
	canAccessManage: boolean;
	isLocalhost: boolean;
	pathname: string | null;
}

export const UserProfile = ({
	pickleballHref,
	tournamentHref,
	leaguesHref,
	ssoLink,
	user,
	canAccessManage,
	isLocalhost,
	pathname
}: UserProfileProps) => {
	const router = useRouter();
	const [open, setOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const userNameClasses = 'ml-1 text-sm font-medium text-white';

	const profileImgArray = user && user.images ? user.images.filter((image: any) => image.type === 'profileImg') : [];
	const profileImgObj = profileImgArray[0];

	const profilePath = `${isLocalhost ? `http://${pickleballHref}` : `https://${pickleballHref}`}/players/${user.slug || user.uuid}`;
	const paymentsPath = `${profilePath}/profile/payments`;
	const myPickleballPath = `${profilePath}/my-pickleball`;
	const myTournamentsPath = `${tournamentHref}/my-tournaments`;
	const myLeaguesPath = `${leaguesHref}/leagues/my-leagues`;
	const managePath = `https://manage.${pickleballHref}`;

	const handleLogout = () => {
		let baseURL = window.location.href;
		if (pathname?.startsWith('/players/')) {
			baseURL = `${window.location.origin}/players`;
		}
		const redirectUrl = isLocalhost ? `/logout` : `${ssoLink}/sessions/revoke?continue=${encodeURIComponent(baseURL)}`;
		router.push(redirectUrl);
	};

	const isProPlayer = +user.proLevelId !== 4 && +user.proLevelId !== 0;

	return (
		<div className="relative" ref={dropdownRef}>
			<div onClick={() => setOpen((prev) => !prev)} className="flex cursor-pointer items-center gap-1">
				<Avatar imageUrl={profileImgObj ? getCDNURL(profileImgObj.url, 24, 24, 0, !isProPlayer) : ''} size="xs" />
				<span className={userNameClasses}>{user.firstName}</span>
				<FontAwesomeIcon icon={faAngleDown} size="xs" className="text-white" />
			</div>

			{open && (
				<div className="absolute right-0 mt-1 py-[6px] z-[150] pl-[6px] font-normal text-sm text-black w-56 rounded border-none bg-white shadow-md">
					<UserProfileItem
						label={'My Pickleball'}
						onClick={() => {
							setOpen(false);
							router.push(myPickleballPath);
						}}
						testId="pre-header-user-profile-my-pickleball"
					>
						<FontAwesomeIcon icon={faSquarePollVertical} size="sm" width="20" />
					</UserProfileItem>
					<UserProfileItem
						label={'My Profile'}
						onClick={() => {
							setOpen(false);
							router.push(profilePath);
						}}
						testId="pre-header-user-profile-my-profile"
					>
						<FontAwesomeIcon icon={faUser} size="sm" width="20" />
					</UserProfileItem>
					<UserProfileItem
						label={'My Tournaments'}
						onClick={() => {
							setOpen(false);
							router.push(myTournamentsPath);
						}}
						testId="pre-header-user-profile-my-tournaments"
					>
						<SVGMyTournament />
					</UserProfileItem>
					<UserProfileItem
						label={'My Leagues'}
						onClick={() => {
							setOpen(false);
							router.push(myLeaguesPath);
						}}
						testId="pre-header-user-profile-my-leagues"
					>
						<SVGMyLeagues />
					</UserProfileItem>
					<UserProfileItem
						label={'Payments'}
						onClick={() => {
							setOpen(false);
							router.push(paymentsPath);
						}}
						testId="pre-header-user-profile-payments"
					>
						<FontAwesomeIcon icon={faCreditCard} size="sm" width="20" />
					</UserProfileItem>

					{canAccessManage && (
						<UserProfileItem
							label={'Manage'}
							onClick={() => {
								setOpen(false);
								router.push(managePath);
							}}
							testId="pre-header-user-profile-manage"
						>
							<FontAwesomeIcon icon={faTableColumns} size="sm" width="20" />
						</UserProfileItem>
					)}
					<UserProfileItem
						label={'Logout'}
						onClick={() => {
							setOpen(false);
							handleLogout();
						}}
						testId="pre-header-user-profile-logout"
					>
						<FontAwesomeIcon icon={faSignOut} size="sm" width="20" />
					</UserProfileItem>
				</div>
			)}
		</div>
	);
};
